import React from "react";
import {
  Box,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowForwardIosRounded } from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { AI_SEARCH_URL, HISTORY_URL } from "../../../utils/routes";
import { useSelector } from "react-redux";
import DotsLoader from "../../../components/DotsLoader";
import { usePostHog } from "posthog-js/react";
import { CLICK_VIEW_ALL_SESSIONS } from "../../../utils/posthogEvents";

const useStyles = makeStyles()((theme, { expanded }, classes) => ({
  activeSessionItem: {
    position: "relative",
    background: expanded
      ? `linear-gradient(to right, transparent 0px, transparent 0px, ${theme.palette.primary.lightBg} 0px)`
      : theme.palette.primary.lightBg,
    borderRadius: 6,
    [`& .${classes.sessionLabelText}`]: {
      fontWeight: 500,
      background: theme.palette.light,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
  },
  sessionItem: {
    padding: expanded ? "4px 8px 4px 12px" : "4px 8px", // Left indent for sub-items (increased for proper spacing)
    borderRadius: 6,
    minHeight: 28,
    marginBottom: 2,
    marginLeft: 12,
    position: "relative",
  },
  collapsedSessionItem: {
    padding: "4px 8px",
    justifyContent: "center",
  },
  sessionLabel: {
    fontSize: 12,
    maxWidth: expanded ? 180 : 50,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sessionLabelText: {},
  viewAllItem: {
    padding: expanded ? "4px 8px 4px 12px" : "4px 8px", // Left indent for sub-items (increased for proper spacing)
    borderRadius: 6,
    minHeight: 28,
    marginBottom: 2,
    marginLeft: 12,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  viewAllText: {
    fontSize: 12,
    opacity: 0.8,
  },
  endIcon: {
    fontSize: 12,
    opacity: 0.6,
  },
  activeViewAllItem: {
    background: expanded
      ? `linear-gradient(to right, transparent 0px, transparent 0px, ${theme.palette.primary.lightBg} 0px)`
      : theme.palette.primary.lightBg,
    borderRadius: 6,
    [`& .${classes.viewAllText}`]: {
      fontWeight: 500,
      background: theme.palette.light,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
  },
}));

function RecentSessions({ sessions, expanded, totalSessionsCount }) {
  const posthog = usePostHog();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const { classes } = useStyles({ expanded });
  const { isAnswerStreaming } = useSelector((state) => state.session);

  const handleSessionClick = (sessionId) => {
    history.push({
      pathname: `${AI_SEARCH_URL}/${sessionId}`,
    });
  };

  const handleViewAllClick = () => {
    history.push({
      pathname: HISTORY_URL,
    });
    posthog.capture(CLICK_VIEW_ALL_SESSIONS);
  };

  const truncateTitle = (title, maxLength) => {
    if (!title) return "Untitled";
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + "...";
  };

  if (!sessions || sessions.length === 0) {
    return null;
  }

  return (
    <Box
      pl={expanded ? 1.5 : 0}
      sx={{
        position: "relative",
        "&::before": expanded
          ? {
              content: '""',
              position: "absolute",
              left: "20px",
              top: 0,
              bottom: 0,
              width: "2px",
              backgroundColor: theme.palette.text.secondary,
              opacity: 0.3,
            }
          : {},
      }}>
      {sessions.map((session) => {
        const isSelected =
          location.pathname === `${AI_SEARCH_URL}/${session.id}`;
        const maxTitleLength = expanded ? 30 : 10;

        const menuItem = (
          <MenuItem
            key={session.id}
            className={`${isSelected ? classes.activeSessionItem : ""} ${
              classes.sessionItem
            } ${!expanded ? classes.collapsedSessionItem : ""}`}
            sx={{
              cursor: "pointer",
            }}
            disabled={isAnswerStreaming && !isSelected}
            onClick={() => handleSessionClick(session.id)}>
            {/* {isAnswerStreaming && isSelected && (
              <ListItemIcon sx={{ minWidth: `24px !important` }}>
                <DotsLoader size={20} />
              </ListItemIcon>
            )} */}
            {isAnswerStreaming && isSelected && (
              <Typography
                fontSize={12}
                color="textSecondary"
                sx={{ minWidth: 12 }}>
                <span className="loadingStyle"></span>
              </Typography>
            )}
            <Typography
              className={`${classes.sessionLabel}`}
              color="textSecondary"
              title={session.title}>
              <span className={classes.sessionLabelText}>
                {truncateTitle(session.title, maxTitleLength)}
              </span>
            </Typography>
          </MenuItem>
        );

        return (
          <Tooltip
            key={session.id}
            title={session.title || "Untitled"}
            arrow
            disableHoverListener={expanded}
            placement="right">
            {menuItem}
          </Tooltip>
        );
      })}

      {/* View All item if there are more than 3 sessions */}
      {totalSessionsCount > 3 && (
        <Tooltip
          title="View All Sessions"
          arrow
          disableHoverListener={expanded}
          placement="right">
          <MenuItem
            className={`${classes.viewAllItem} ${
              location.pathname.startsWith(HISTORY_URL)
                ? classes.activeViewAllItem
                : ""
            }`}
            sx={{
              cursor: "pointer",
            }}
            disabled={isAnswerStreaming}
            onClick={handleViewAllClick}>
            <Typography className={classes.viewAllText} color="textSecondary">
              View All ({totalSessionsCount})
            </Typography>
            {expanded && <ArrowForwardIosRounded className={classes.endIcon} />}
          </MenuItem>
        </Tooltip>
      )}
    </Box>
  );
}

export default RecentSessions;
